import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import QRCode from 'qrcode'
import type { Church, OccurrenceWithEvent } from '@fellowship/shared'
import { useChurch, useChurchEvents } from '../hooks/useChurches'
import { AdminShell } from '../components/admin/AdminShell'
import { AdminPageHeader, AdminCard, AdmBtn } from '../components/admin/AdminAtoms'
import { AdmIcn } from '../components/admin/AdminIcons'
import { getEventType } from '../components/ui/eventTypes'
import { Wordmark } from '../components/ui/Wordmark'

const VERSE_KEY = 'bulletin_verse'
const DEFAULT_VERSE = '"Come, let us bow down in worship, let us kneel before the Lord our Maker." — Psalm 95:6'

function formatEventDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

export default function BulletinPage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: churchData, isLoading: churchLoading } = useChurch(slug)
  const { data: eventsData, isLoading: eventsLoading } = useChurchEvents(slug)

  const [verse, setVerse] = useState(() => localStorage.getItem(VERSE_KEY) ?? DEFAULT_VERSE)
  const [showServiceTimes, setShowServiceTimes] = useState(true)
  const [showEvents, setShowEvents] = useState(true)
  const [showGiving, setShowGiving] = useState(true)
  const [showVerse, setShowVerse] = useState(true)
  const [maxEvents, setMaxEvents] = useState(4)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)

  const church = churchData?.data
  const profileUrl = slug ? `${window.location.origin}/churches/${slug}` : ''

  const allOccurrences = (eventsData?.data ?? [])
    .filter((o) => !o.isCancelled)
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())

  const occurrences = allOccurrences.slice(0, maxEvents)

  useEffect(() => {
    localStorage.setItem(VERSE_KEY, verse)
  }, [verse])

  useEffect(() => {
    if (!profileUrl) return
    QRCode.toDataURL(profileUrl, { width: 240, margin: 1, color: { dark: '#1a1a1a', light: '#ffffff' } })
      .then(setQrDataUrl)
      .catch(() => {})
  }, [profileUrl])

  const isLoading = churchLoading || eventsLoading

  return (
    <AdminShell>
      <style>{`
        @media print {
          .bulletin-no-print { display: none !important; }
          body { background: white !important; }
          @page { size: 5.5in 8.5in; margin: 0; }
          .bulletin-paper { box-shadow: none !important; border: none !important; margin: 0 !important; border-radius: 0 !important; }
        }
      `}</style>

      <div className="bulletin-no-print">
        <AdminPageHeader
          eyebrow="Bulletin insert"
          title="Bulletin"
          titleItalic="insert."
          sub="A half-page print insert for your welcome table or announcements."
          actions={
            <AdmBtn variant="primary" icon="download" onClick={() => window.print()}>
              Print / Save as PDF
            </AdmBtn>
          }
        />
      </div>

      {isLoading ? (
        <div className="text-[13px] text-muted mt-4">Loading…</div>
      ) : (
        <div className="bulletin-no-print flex gap-6 items-start">
          {/* ── Settings panel ───────────────────────────────────────────── */}
          <div className="w-[260px] shrink-0 flex flex-col gap-4">
            <AdminCard title="Sections">
              <div className="flex flex-col gap-2.5">
                {([
                  { key: 'events', label: 'Upcoming events', state: showEvents, set: setShowEvents },
                  { key: 'serviceTimes', label: 'Service times', state: showServiceTimes, set: setShowServiceTimes },
                  { key: 'giving', label: 'Giving link', state: showGiving, set: setShowGiving },
                  { key: 'verse', label: 'Scripture verse', state: showVerse, set: setShowVerse },
                ] as const).map(({ key, label, state, set }) => (
                  <label key={key} className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={state}
                      onChange={(e) => set(e.target.checked)}
                      className="w-[15px] h-[15px] rounded cursor-pointer accent-primary"
                    />
                    <span className="text-[13px] text-ink-2">{label}</span>
                  </label>
                ))}
              </div>
            </AdminCard>

            {showEvents && (
              <AdminCard title="Events to show">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setMaxEvents((v) => Math.max(1, v - 1))}
                    className="w-[28px] h-[28px] rounded-full border border-line-soft bg-surface flex items-center justify-center cursor-pointer text-ink-2"
                  >
                    <AdmIcn name="minus" size={13} />
                  </button>
                  <span className="text-[14px] font-semibold w-[24px] text-center">{maxEvents}</span>
                  <button
                    type="button"
                    onClick={() => setMaxEvents((v) => Math.min(8, v + 1))}
                    className="w-[28px] h-[28px] rounded-full border border-line-soft bg-surface flex items-center justify-center cursor-pointer text-ink-2"
                  >
                    <AdmIcn name="plus" size={13} />
                  </button>
                  <span className="text-[12px] text-muted ml-1">of {allOccurrences.length}</span>
                </div>
              </AdminCard>
            )}

            {showVerse && (
              <AdminCard title="Scripture verse">
                <textarea
                  value={verse}
                  onChange={(e) => setVerse(e.target.value)}
                  rows={4}
                  placeholder="Enter a verse…"
                  className="w-full text-[12.5px] text-ink-2 resize-none border border-line-soft rounded-lg px-3 py-2 bg-bg outline-none focus:border-primary transition-colors leading-[1.5] box-border"
                />
                <button
                  type="button"
                  onClick={() => setVerse(DEFAULT_VERSE)}
                  className="mt-1.5 text-[11.5px] text-muted bg-transparent border-0 cursor-pointer underline underline-offset-2 p-0"
                >
                  Reset to default
                </button>
              </AdminCard>
            )}

            <AdminCard title="Service times">
              {(church?.serviceTimes?.length ?? 0) === 0 ? (
                <div className="text-[12px] text-muted">
                  No service times set.{' '}
                  <a href={`/churches/${slug}/edit`} className="text-primary underline underline-offset-2">
                    Add them in Church profile.
                  </a>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {church?.serviceTimes.map((st, i) => (
                    <div key={i} className="text-[12.5px] text-ink-2">
                      <span className="font-semibold">{st.name}</span>
                      {st.day ? ` · ${st.day}` : ''}
                      {st.time ? ` · ${st.time}` : ''}
                    </div>
                  ))}
                </div>
              )}
            </AdminCard>
          </div>

          {/* ── Live preview ─────────────────────────────────────────────── */}
          <div className="flex-1 flex justify-center">
            <BulletinPreview
              church={church}
              occurrences={occurrences}
              qrDataUrl={qrDataUrl}
              profileUrl={profileUrl}
              showEvents={showEvents}
              showServiceTimes={showServiceTimes}
              showGiving={showGiving}
              showVerse={showVerse}
              verse={verse}
            />
          </div>
        </div>
      )}

    </AdminShell>
  )
}

// ─── Bulletin paper component (used for both preview and print) ────────────────

interface BulletinPreviewProps {
  church: Church | undefined
  occurrences: OccurrenceWithEvent[]
  qrDataUrl: string | null
  profileUrl: string
  showEvents: boolean
  showServiceTimes: boolean
  showGiving: boolean
  showVerse: boolean
  verse: string
}

function BulletinPreview({
  church,
  occurrences,
  qrDataUrl,
  profileUrl,
  showEvents,
  showServiceTimes,
  showGiving,
  showVerse,
  verse,
}: BulletinPreviewProps) {
  return (
    <div
      className="bulletin-paper"
      style={{
        width: '5.5in',
        background: '#fff',
        borderRadius: '16px',
        border: '1px solid #E5DDD0',
        boxShadow: '0 20px 60px -10px rgba(60,40,10,0.18)',
        padding: '40px 44px 36px',
        fontFamily: 'Georgia, "Times New Roman", serif',
        color: '#1A1610',
      }}
    >
      {/* Church header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '18px', paddingBottom: '20px', marginBottom: '20px', borderBottom: '1.5px solid #EDE8D8' }}>
        {church?.logoUrl ? (
          <img
            src={church.logoUrl}
            alt={`${church.name} logo`}
            style={{ width: '60px', height: '60px', borderRadius: '12px', objectFit: 'cover', flexShrink: 0 }}
          />
        ) : (
          <div style={{ width: '60px', height: '60px', borderRadius: '12px', flexShrink: 0, background: '#4F3CC9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', fontWeight: 700, color: '#fff' }}>
            {church?.name?.charAt(0) ?? 'C'}
          </div>
        )}
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, lineHeight: 1.2, color: '#1A1610' }}>{church?.name}</h1>
          {(church?.city || church?.state) && (
            <div style={{ fontSize: '12.5px', marginTop: '2px', color: '#6B5E3E' }}>
              {[church.city, church.state].filter(Boolean).join(', ')}
            </div>
          )}
          {church?.websiteUrl && (
            <div style={{ fontSize: '11px', marginTop: '2px', color: '#A09070' }}>{church.websiteUrl}</div>
          )}
        </div>
        <div style={{ flexShrink: 0, textAlign: 'right' }}>
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="QR code to church profile" style={{ width: '80px', height: '80px', borderRadius: '8px' }} />
          ) : (
            <div style={{ width: '80px', height: '80px', borderRadius: '8px', background: '#F5F0E6' }} />
          )}
          <div style={{ fontSize: '10px', color: '#A09070', marginTop: '4px', textAlign: 'center' }}>Scan to RSVP</div>
        </div>
      </div>

      {/* Service times */}
      {showServiceTimes && (church?.serviceTimes?.length ?? 0) > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#A09070', marginBottom: '10px' }}>
            Service Times
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {church!.serviceTimes.map((st, i) => (
              <div key={i} style={{ padding: '6px 12px', background: '#F7F3EC', borderRadius: '999px', border: '1px solid #EDE8D8' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#1A1610' }}>{st.name}</span>
                {(st.day || st.time) && (
                  <span style={{ fontSize: '11.5px', color: '#6B5E3E' }}>
                    {st.day ? ` · ${st.day}` : ''}{st.time ? ` · ${st.time}` : ''}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming events */}
      {showEvents && (
        <div style={{ marginBottom: showGiving || showVerse ? '20px' : '0' }}>
          <div style={{ fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#A09070', marginBottom: '12px' }}>
            Upcoming Events
          </div>
          {occurrences.length === 0 ? (
            <div style={{ fontSize: '12.5px', color: '#A09070' }}>No upcoming events scheduled.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {occurrences.map((occ) => {
                const et = getEventType(occ.event.eventType)
                return (
                  <div key={occ.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <span aria-label={et.label} style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', background: et.soft, color: et.color }}>
                      {et.glyph}
                    </span>
                    <div>
                      <div style={{ fontSize: '13.5px', fontWeight: 600, lineHeight: 1.25, color: '#1A1610' }}>
                        {occ.overrideTitle ?? occ.event.title}
                      </div>
                      <div style={{ fontSize: '11.5px', marginTop: '2px', color: '#6B5E3E' }}>
                        {formatEventDate(occ.startsAt)}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Giving link */}
      {showGiving && church?.givingUrl && (
        <div style={{ marginBottom: showVerse ? '20px' : '0', padding: '10px 14px', background: '#F7F3EC', border: '1px solid #EDE8D8', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#4F3CC9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ color: '#fff', fontSize: '14px' }}>♥</span>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#A09070', marginBottom: '2px' }}>Give Online</div>
            <div style={{ fontSize: '12px', color: '#4F3CC9' }}>{church.givingUrl}</div>
          </div>
        </div>
      )}

      {/* Verse */}
      {showVerse && verse && (
        <div style={{ marginTop: showEvents || showServiceTimes || (showGiving && church?.givingUrl) ? '0' : '0', padding: '14px 16px', background: '#F7F3EC', borderLeft: '3px solid #4F3CC9', borderRadius: '0 8px 8px 0' }}>
          <div style={{ fontSize: '12.5px', fontStyle: 'italic', lineHeight: 1.55, color: '#4A3D28' }}>{verse}</div>
        </div>
      )}

      {/* Footer */}
      <div style={{ marginTop: '20px', paddingTop: '14px', borderTop: '1px solid #EDE8D8', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: '10px', color: '#C0B49A' }}>{profileUrl}</div>
        <WordmarkSmall />
      </div>
    </div>
  )
}

function WordmarkSmall() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', opacity: 0.45 }}>
      <Wordmark size={13} />
    </div>
  )
}
